import { Reducer } from 'redux';

import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import {
  GET_SURVEY_REQUEST,
  GET_SURVEY_SUCCESS,
  GET_SURVEY_FAILED,
  RESET_SURVEYS,
  SAVE_TOPICS_FOR_SURVEY_REQUEST,
  SAVE_TOPICS_FOR_SURVEY_SUCCESS,
  SAVE_TOPICS_FOR_SURVEY_FAILED,
  GetSurveys,
  AddSurvey,
  GetPulseSurveys,
  GetGroupSurveys,
  GetPulseSurveysSearch,
  GetGroupSurveysSearch,
  CopySurvey,
  DeleteSurvey,
  EditSurvey,
  GetPeopleSurveys,
  GetPeopleSurveysSearch,
} from '~src/modules/surveys/constants/actionTypes';
import { Survey, SurveyExtended } from '~src/modules/surveys/interfaces';
import { ThunkSuccessAction, ThunkAnyAction } from '~src/services/apiMiddleware';
import { PaginationI } from '~src/interfaces/pagination.interface';
import lStorage from '~src/services/localStorage.service';
import { LocalStorageKeys } from '~src/interfaces/localSotrage.types';
import { fetchExports } from '~src/store/exports/action.types';

export interface SurveyTypeData {
  items: SurveyExtended[];
  itemsSearch: SurveyExtended[];
  loading: boolean;
  pagination: PaginationI | null;
  paginationSearch: PaginationI | null;
}

function getDefaultSurveyTypeData(): SurveyTypeData {
  return {
    items: [],
    itemsSearch: [],
    loading: false,
    pagination: null,
    paginationSearch: null,
  };
}

export interface SurveysState {
  [SurveySubjectTypes.General]: SurveyTypeData;
  [SurveySubjectTypes.Groups]: SurveyTypeData;
  [SurveySubjectTypes.People]: SurveyTypeData;
  copiedSurvey: Survey | null;
  error: Record<string, string>;
  items: SurveyExtended[];
  loading: boolean;
  loadingAdd: boolean;
  loadingCopy: boolean;
  loadingDel: boolean;
  loadingEdit: boolean;
  loadingQuestionsSave: boolean;
  loadingSurvey: boolean;
  pagination: PaginationI | null;
  pulseLoading: boolean;
  survey: Survey | null;
}

export function getInitialSurveysState(): SurveysState {
  return {
    [SurveySubjectTypes.General]: getDefaultSurveyTypeData(),
    [SurveySubjectTypes.Groups]: getDefaultSurveyTypeData(),
    [SurveySubjectTypes.People]: getDefaultSurveyTypeData(),
    copiedSurvey: null,
    error: {},
    items: [],
    loading: false,
    loadingAdd: false,
    loadingCopy: false,
    loadingDel: false,
    loadingEdit: false,
    loadingQuestionsSave: false,
    loadingSurvey: false,
    pagination: null,
    pulseLoading: false,
    survey: null,
  };
}

const deleteSurvey = (
  items: SurveyExtended[],
  pagination: PaginationI | null,
  { id }: ThunkSuccessAction,
): { items: SurveyExtended[]; pagination: PaginationI | null } => {
  if (!pagination) {
    return { items, pagination };
  }
  const surveys = [...items];
  const paginationCopy = { ...pagination };
  const deletedIndex = surveys.findIndex((item) => item.id === id);
  lStorage.removeItem(
    LocalStorageKeys.OneTimeNotifications,
    `${fetchExports.Success}.${surveys[deletedIndex].id}`,
  );
  surveys.splice(deletedIndex, 1);
  paginationCopy.count -= 1;
  paginationCopy.total -= 1;
  return { items: surveys, pagination: paginationCopy };
};

const getAllAndLoadMoreItems = (
  stateItems: SurveyExtended[],
  action: ThunkSuccessAction,
): SurveyExtended[] => {
  let data = action.response.data.items;
  if (data === undefined && action.response.data.result) {
    data = action.response.data.result.survey_list;
  }
  return action.loadMore ? [...stateItems, ...data] : data;
};

const surveysReducer: Reducer<SurveysState, ThunkAnyAction> = (
  state: SurveysState = getInitialSurveysState(),
  action: ThunkAnyAction,
): SurveysState => {
  switch (action.type) {
    case GetSurveys.Request:
      return {
        ...state,
        loading: true,
      };
    case GetSurveys.Success:
      return {
        ...state,
        loading: false,
        items: getAllAndLoadMoreItems(state.items, action),
        pagination: action.response.data.pagination,
      };
    case GetSurveys.Failed:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case GetPulseSurveys.Request:
    case GetPulseSurveysSearch.Request:
      return {
        ...state,
        general: {
          ...state.general,
          loading: true,
        },
      };
    case GetPulseSurveys.Success:
      return {
        ...state,
        general: {
          ...state.general,
          loading: false,
          items: getAllAndLoadMoreItems(state.general.items, action),
          pagination: action.response.data.pagination,
        },
      };
    case GetPulseSurveys.Failed:
    case GetPulseSurveysSearch.Failed:
      return {
        ...state,
        general: {
          ...state.general,
          loading: false,
        },
      };
    case GetPulseSurveysSearch.Success:
      return {
        ...state,
        general: {
          ...state.general,
          loading: false,
          itemsSearch: getAllAndLoadMoreItems(state.general.itemsSearch, action),
          paginationSearch: action.response.data.pagination,
        },
      };

    case GetGroupSurveys.Request:
    case GetGroupSurveysSearch.Request:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: true,
        },
      };
    case GetGroupSurveys.Success:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: false,
          items: getAllAndLoadMoreItems(state.groups.items, action),
          pagination: action.response.data.pagination,
        },
      };
    case GetGroupSurveys.Failed:
    case GetGroupSurveysSearch.Failed:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: false,
        },
      };
    case GetGroupSurveysSearch.Success:
      return {
        ...state,
        groups: {
          ...state.groups,
          loading: false,
          itemsSearch: getAllAndLoadMoreItems(state.groups.itemsSearch, action),
          paginationSearch: action.response.data.pagination,
        },
      };

    case GetPeopleSurveys.Request:
    case GetPeopleSurveysSearch.Request:
      return {
        ...state,
        people: {
          ...state.people,
          loading: true,
        },
      };
    case GetPeopleSurveys.Success:
      return {
        ...state,
        people: {
          ...state.people,
          loading: false,
          items: getAllAndLoadMoreItems(state.people.items, action),
          pagination: action.response.data.pagination,
        },
      };
    case GetPeopleSurveys.Failed:
    case GetPeopleSurveysSearch.Failed:
      return {
        ...state,
        people: {
          ...state.people,
          loading: false,
        },
      };
    case GetPeopleSurveysSearch.Success:
      return {
        ...state,
        people: {
          ...state.people,
          loading: false,
          itemsSearch: getAllAndLoadMoreItems(state.people.itemsSearch, action),
          paginationSearch: action.response.data.pagination,
        },
      };

    case GET_SURVEY_REQUEST:
      return {
        ...state,
        loadingSurvey: true,
      };
    case GET_SURVEY_SUCCESS:
      return {
        ...state,
        survey: action.response.data,
        loadingSurvey: false,
      };
    case GET_SURVEY_FAILED:
      return {
        ...state,
        loadingSurvey: false,
      };
    case AddSurvey.Request:
      return {
        ...state,
        loadingAdd: true,
      };
    case AddSurvey.Success:
      return {
        ...state,
        loadingAdd: false,
      };
    case AddSurvey.Failed:
      return {
        ...state,
        loadingAdd: false,
      };
    case RESET_SURVEYS:
      return {
        ...state,
        items: [],
      };
    case EditSurvey.Request:
      return {
        ...state,
        loadingEdit: true,
      };
    case EditSurvey.Success:
      return {
        ...state,
        loadingEdit: false,
        survey: action.response.data,
      };
    case EditSurvey.Failed:
      return {
        ...state,
        loadingEdit: false,
      };
    case DeleteSurvey.Request:
      return {
        ...state,
        loadingDel: true,
      };
    case DeleteSurvey.Success:
      return {
        ...state,
        ...deleteSurvey(state.items, state.pagination, action),
        loadingDel: false,
      };
    case DeleteSurvey.Failed:
      return {
        ...state,
        loadingDel: false,
      };
    case SAVE_TOPICS_FOR_SURVEY_REQUEST:
      return {
        ...state,
        loadingQuestionsSave: true,
      };
    case SAVE_TOPICS_FOR_SURVEY_SUCCESS:
      return {
        ...state,
        survey: action.response.data,
        loadingQuestionsSave: false,
      };

    case SAVE_TOPICS_FOR_SURVEY_FAILED:
      return {
        ...state,
        loadingQuestionsSave: false,
      };
    case CopySurvey.Request:
      return {
        ...state,
        loadingCopy: true,
      };
    case CopySurvey.Success:
      return {
        ...state,
        loadingCopy: false,
        copiedSurvey: action.response.data,
      };
    case CopySurvey.Failed:
      return {
        ...state,
        loadingCopy: false,
      };
    default:
      return state;
  }
};

export default surveysReducer;
